import { useEffect, useState } from "react";
import { api, formatBRL } from "../../lib/api";
import { Plus, Trash2, Star, Edit2, Copy, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", type: "coworking_flex", category: "", pricing_mode: "monthly",
  price_monthly: 0, price_daily: 0, price_hourly: 0, price_package: 0,
  package_hours: 0, validity_days: 30,
  description: "", features: "", max_hours_per_month: 0, active: true, highlight: false,
  display_order: 0,
};

const TYPES = [
  ["coworking_flex", "Coworking Flex"],
  ["day_use", "Day Use"],
  ["pacote_horas", "Pacote de Horas"],
  ["mesa_fixa", "Mesa Fixa"],
  ["sala_privativa", "Sala Privativa"],
  ["escritorio_executivo", "Escritório Executivo"],
  ["escritorio_executivo_premium", "Escritório Executivo Premium"],
  ["consultorio", "Consultório"],
  ["escritorio_virtual", "Escritório Virtual"],
  ["sala_reuniao", "Sala de Reunião"],
  ["auditorio", "Auditório"],
  ["avulso", "Avulso"],
];

const PRICING_MODES = [
  ["monthly", "Mensal"],
  ["daily", "Diário"],
  ["hourly", "Hora"],
  ["package", "Pacote (preço total)"],
  ["custom", "Sob consulta"],
];

export default function Planos() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/plans").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_monthly: +form.price_monthly || 0,
      price_daily: +form.price_daily || null,
      price_hourly: +form.price_hourly || null,
      price_package: +form.price_package || null,
      package_hours: +form.package_hours || null,
      validity_days: +form.validity_days || null,
      max_hours_per_month: +form.max_hours_per_month || null,
      display_order: +form.display_order || 0,
      features: typeof form.features === "string"
        ? form.features.split("\n").map((s) => s.trim()).filter(Boolean)
        : form.features,
    };
    try {
      if (editingId) {
        await api.put(`/plans/${editingId}`, payload);
        toast.success("Plano atualizado");
      } else {
        await api.post("/plans", payload);
        toast.success("Plano criado");
      }
      setShowForm(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
  };

  const edit = (p) => {
    setForm({
      ...empty,
      ...p,
      features: Array.isArray(p.features) ? p.features.join("\n") : (p.features || ""),
    });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/plans/${id}/duplicate`);
      toast.success("Plano duplicado (rascunho inativo)");
      load();
    } catch { toast.error("Erro ao duplicar"); }
  };

  const toggleActive = async (id) => {
    try {
      const { data } = await api.patch(`/plans/${id}/toggle`);
      toast.success(data.active ? "Plano ativado" : "Plano inativado");
      load();
    } catch { toast.error("Erro ao alternar status"); }
  };

  const remove = async (id) => {
    if (!confirm("Remover este plano?")) return;
    await api.delete(`/plans/${id}`);
    toast.success("Removido"); load();
  };

  const cancel = () => { setShowForm(false); setForm(empty); setEditingId(null); };

  const priceLabel = (p) => {
    if (p.pricing_mode === "package") return `${formatBRL(p.price_package || 0)} · ${p.package_hours || 0}h`;
    if (p.pricing_mode === "daily" || p.price_daily) return `${formatBRL(p.price_daily || 0)}/dia`;
    if (p.pricing_mode === "hourly" || p.price_hourly) return `${formatBRL(p.price_hourly || 0)}/hora`;
    if (p.pricing_mode === "custom") return "Sob consulta";
    return `${formatBRL(p.price_monthly || 0)}/mês`;
  };

  return (
    <div data-testid="admin-planos">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Planos</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Catálogo de Planos</h1>
        </div>
        <button onClick={() => { setShowForm(!showForm); if (showForm) cancel(); }} className="w-full sm:w-auto bg-[#14213D] text-white px-5 py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="new-plan-btn">
          <Plus size={16} /> {editingId ? "Editando…" : "Novo Plano"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="plan-form">
          <input required placeholder="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="plan-name" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Categoria (ex: Coworking)" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="plan-category" />

          <select value={form.pricing_mode} onChange={(e) => setForm({ ...form, pricing_mode: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="plan-pricing-mode">
            {PRICING_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Mensal (R$)" value={form.price_monthly} onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input type="number" step="0.01" placeholder="Diário (R$)" value={form.price_daily} onChange={(e) => setForm({ ...form, price_daily: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input type="number" step="0.01" placeholder="Hora (R$)" value={form.price_hourly} onChange={(e) => setForm({ ...form, price_hourly: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input type="number" step="0.01" placeholder="Pacote (R$ total)" value={form.price_package} onChange={(e) => setForm({ ...form, price_package: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="plan-price-package" />
          <input type="number" placeholder="Horas do pacote" value={form.package_hours} onChange={(e) => setForm({ ...form, package_hours: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="plan-package-hours" />
          <input type="number" placeholder="Validade (dias)" value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="plan-validity" />
          <input type="number" placeholder="Ordem de exibição" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />

          <textarea placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm md:col-span-3" rows={2} data-testid="plan-description" />
          <textarea placeholder="Benefícios / features (uma por linha)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm md:col-span-3" rows={4} data-testid="plan-features" />

          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} /> Destacar plano</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Ativo</label>
          <div />

          <div className="md:col-span-3 flex flex-col sm:flex-row gap-3">
            <button type="submit" className="bg-[#D4AF37] text-[#14213D] font-bold py-2.5 px-6 rounded text-sm flex-1 sm:flex-none" data-testid="save-plan">
              {editingId ? "Atualizar plano" : "Salvar plano"}
            </button>
            <button type="button" onClick={cancel} className="border border-gray-300 px-6 py-2.5 rounded text-sm">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...plans].sort((a, b) =>
          (a.category || a.type || "").localeCompare(b.category || b.type || "", "pt-BR") ||
          (a.package_hours || 9999) - (b.package_hours || 9999) ||
          (a.name || "").localeCompare(b.name || "", "pt-BR")
        ).map((p) => (
          <div key={p.id} className={`bg-white border rounded-lg p-5 ${p.highlight ? "border-[#D4AF37]" : "border-gray-200"} ${p.active === false ? "opacity-60" : ""}`} data-testid={`plan-${p.id}`}>
            <div className="flex justify-between items-start mb-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{p.category || p.type}</div>
                <div className="font-display text-lg text-[#14213D] mt-1">{p.name}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {p.highlight && <Star size={14} className="text-[#D4AF37]" />}
                {p.active === false && <span className="text-[9px] uppercase font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Inativo</span>}
              </div>
            </div>
            <div className="text-xs text-gray-500 mb-3 line-clamp-2">{p.description}</div>
            <div className="font-display text-2xl text-[#14213D] mb-4">{priceLabel(p)}</div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => edit(p)} className="border border-gray-200 rounded py-1.5 text-xs flex items-center justify-center gap-1 hover:bg-gray-50" data-testid={`edit-plan-${p.id}`}><Edit2 size={12} /> Editar</button>
              <button onClick={() => duplicate(p.id)} className="border border-gray-200 rounded py-1.5 text-xs flex items-center justify-center gap-1 hover:bg-gray-50" data-testid={`dup-plan-${p.id}`}><Copy size={12} /> Duplicar</button>
              <button onClick={() => toggleActive(p.id)} className={`border rounded py-1.5 text-xs flex items-center justify-center gap-1 ${p.active === false ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-amber-200 text-amber-700 hover:bg-amber-50"}`} data-testid={`toggle-plan-${p.id}`}>
                {p.active === false ? <><Power size={12} /> Ativar</> : <><PowerOff size={12} /> Inativar</>}
              </button>
              <button onClick={() => remove(p.id)} className="border border-red-200 text-red-600 rounded py-1.5 text-xs flex items-center justify-center gap-1 hover:bg-red-50" data-testid={`del-plan-${p.id}`}><Trash2 size={12} /> Remover</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
